import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useMutation, useQuery } from "@tanstack/react-query"
import { validateInvitation, acceptInvitation, rejectInvitation } from "@/lib/http/team"
import { Button } from "@/components/ui/button"
import { ROUTES } from "@/routes/path"
import { useAuth } from "@/hooks/useAuth"
import { useToast } from "@/hooks/useToast"
import { useListOrganizations } from "@/hooks/useOrganization"
import { switchOrganization } from "@/lib/http/organization"
import { CheckCircle, XCircle, AlertCircle, Loader2 } from "lucide-react"
import { toTitleCase } from "@/lib/utils/characters"

type InvitationData = {
  valid: boolean
  email?: string
  organization?: string
  role?: string
  expired: boolean
  exists: boolean
  message?: string
}

export default function InvitePage() {
  const { token: inviteToken } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { token: authToken } = useAuth()
  const isAuthenticated = !!authToken
  const { toast } = useToast()
  const { refetch: refetchOrganizations } = useListOrganizations(!!authToken)
  const {
    data: invitationData,
    isLoading,
    error: validationError,
  } = useQuery({
    queryKey: ["validateInvitation", inviteToken],
    queryFn: () => validateInvitation(inviteToken!),
    enabled: !!inviteToken,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const acceptMutation = useMutation({
    mutationFn: () => acceptInvitation(inviteToken!),
    retry: false,
    onSuccess: async (data) => {
      if (data.success) {
        toast({
          title: "Success!",
          description: "You have successfully joined the organization.",
        })

        // Refetch organizations to get the updated list
        const { data: organizations } = await refetchOrganizations()

        // Find the organization that matches the invitation's organization name
        const newOrganization = organizations?.find((org) => org.name === invitationData?.organization)

        // If found, switch to it; otherwise use the first organization
        const organizationToSwitch = newOrganization || organizations?.[0]

        if (organizationToSwitch) {
          // Switch to the organization on the backend
          await switchOrganization(organizationToSwitch.id)
        }

        // Navigate to studio page
        navigate(ROUTES.DASHBOARD.STUDIO.INDEX)
      } else {
        toast({
          title: "Error",
          description: data.message,
          variant: "destructive",
        })
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          toTitleCase(error?.response?.data?.error) ||
          toTitleCase(error?.response?.data?.message) ||
          "Failed to accept invitation",
        variant: "destructive",
      })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: () => rejectInvitation(inviteToken!),
    retry: false,
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: "Invitation Declined",
          description: "You have declined the invitation.",
        })
        navigate(ROUTES.AUTH.LOGIN)
      } else {
        toast({
          title: "Error",
          description: data.message,
          variant: "destructive",
        })
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          toTitleCase(error?.response?.data?.error) ||
          toTitleCase(error?.response?.data?.message) ||
          "Failed to reject invitation",
        variant: "destructive",
      })
    },
  })

  // Handle validation error by creating error state
  const [errorData, setErrorData] = useState<InvitationData | null>(null)

  useEffect(() => {
    if (validationError) {
      const error = validationError as any
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message

      // Check if error response has validation structure
      const errorResponse = error?.response?.data
      if (errorResponse?.valid === false || errorResponse?.expired) {
        setErrorData({
          valid: false,
          expired: errorResponse.expired || false,
          exists: errorResponse.exists || false,
          message: errorResponse.message || errorMessage || "Invalid invitation token",
        })
      } else {
        // Generic error fallback
        setErrorData({
          valid: false,
          expired: false,
          exists: false,
          message: errorMessage || "Failed to validate invitation. Please try again.",
        })
      }
    }
  }, [validationError])

  const handleAccept = () => {
    if (isAuthenticated) {
      acceptMutation.mutate()
    } else {
      // Store invitation token and redirect based on whether account exists
      localStorage.setItem("pendingInvitation", inviteToken!)
      if ((displayData as any)?.exists) {
        navigate(ROUTES.AUTH.LOGIN)
      } else {
        navigate(ROUTES.AUTH.REGISTER)
      }
    }
  }

  const handleReject = () => {
    if (isAuthenticated) {
      rejectMutation.mutate()
    } else {
      // Store invitation token and redirect to login
      localStorage.setItem("pendingInvitation", inviteToken!)
      navigate(ROUTES.AUTH.LOGIN)
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-zinc-900">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Validating invitation...</p>
        </div>
      </div>
    )
  }

  // Use error data if there's a validation error, otherwise use the query data
  const displayData = errorData || invitationData

  if (!displayData?.valid) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-zinc-900">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
            <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="mb-2 text-2xl font-semibold text-gray-900 dark:text-zinc-100">
            {displayData?.expired ? "Invitation Expired" : "Invalid Invitation"}
          </h1>
          <p className="mb-6 text-gray-600 dark:text-zinc-400">
            {displayData?.message || "This invitation is no longer valid."}
          </p>
          <Button onClick={() => navigate(ROUTES.AUTH.LOGIN)}>Go to Login</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-zinc-900">
      <div className="w-full max-w-md space-y-6 rounded-xl bg-white p-8 text-center shadow-xl dark:bg-zinc-800">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-900/20">
          <CheckCircle className="h-8 w-8 text-gray-600 dark:text-gray-400" />
        </div>

        <h1 className="text-2xl font-semibold text-gray-900 dark:text-zinc-100">You&apos;re Invited!</h1>

        <div className="space-y-2 pb-4 text-gray-600 dark:text-zinc-400">
          <p>
            <span className="font-medium">{displayData.organization}</span> has invited you to join their team.
          </p>
          <p>
            <span className="font-medium">Email:</span> {displayData.email}
          </p>
        </div>

        {!isAuthenticated && (
          <div className="rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/20">
            <div className="flex items-center">
              <AlertCircle className="mr-2 h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                You need to be logged in to accept this invitation.
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={handleReject}
            disabled={acceptMutation.isPending || rejectMutation.isPending}
            className="flex-1"
          >
            {rejectMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <XCircle className="mr-2 h-4 w-4" />
            )}
            Decline
          </Button>
          <Button
            onClick={handleAccept}
            disabled={acceptMutation.isPending || rejectMutation.isPending}
            className="flex-1"
          >
            {acceptMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle className="mr-2 h-4 w-4" />
            )}
            Accept
          </Button>
        </div>
      </div>
    </div>
  )
}
